import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import apiClient from "../../services/apiClient";
import { retrieveWorksheet } from "../worksheets/worksheetSlice";
import WorksheetModal from "../worksheets/components/WorksheetModal/WorksheetModal";
import secret from "../../secret.json";
import "./OfficialBookPage.css";

// 학생 화면으로 책 전체를 미리 본다. 저장하지 않는다(api assignment/official_book_preview.py). 출제 화면에서도 쓴다.
export const openOfficialBookPreview = (bookId, user) => {
  window.open(`${secret.STUDENT_BASE_URL}/preview?official=${bookId}&session=${user?.session || ""}`, "_blank");
};

// 문제지 관리 > 공식 교재(2026-10-20 대표). 지인에듀 공식 파닉스 · 리더스북 · 문법책 · 단어장을 학원이 직접 출제한다.
// 책 본문 · 그림 · 음원은 우리가 떠 둔 사본(api OfficialBook)이라 지인에듀 계정과 상관없이 열린다.
// 목록은 누구에게나 보이고 출제는 정해 둔 계정만 된다(서버 OFFICIAL_BOOK_ALLOWED_NICKNAME_LIST). 미리보기는 서버가 정한 책만(can_preview). 실제 차단은 서버(AssignmentCreateViewSet)가 한다.
// 생김새는 책 톤(파닉스 분홍 · 보라, 등장인물)을 따르고, 종이책 쪽이 그대로 온라인 과제가 된다는 연결을 보여 준다.
const KIND_LIST = [
  { key: "phonics", label: "파닉스", english: "ECHO Phonics" },
  { key: "readers", label: "리더스북", english: "ECHO Readers" },
  // 리더스북 한 권마다 딸린 문법책 · 단어장(지인에듀 7판). 리더스북과 따로 낸다
  { key: "grammar_book", label: "문법책", english: "ECHO Readers Grammar Book" },
  { key: "vocab_book", label: "단어장", english: "ECHO Readers Word Book" },
];

// 종이책과 온라인이 이어지는 세 가지(카드 머리 · 책장 위 안내에 같이 쓴다)
const BRIDGE_LIST = [
  { icon: "book", title: "종이책 쪽 그대로", body: "수업에서 쓰는 책 쪽이 그대로 온라인 과제가 돼요" },
  { icon: "sound", title: "QR 대신 바로 듣기", body: "Listen · Watch QR 자리를 누르면 소리와 입모양 영상" },
  { icon: "pen", title: "쪽 위에서 바로 채점", body: "체크 · 동그라미 · 쓰기를 그 자리에서 매겨요" },
];

// 'Ginedu Phonics · Unit 01 · Tap, Tap, Tap!' → ['Unit 01', 'Tap, Tap, Tap!']
const splitTitle = (title) => {
  const partList = String(title || "").split(" · ");
  if (partList.length >= 3) return [partList[1], partList.slice(2).join(" · ")];
  return ["", title];
};

const OfficialBookPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [kind, setKind] = useState("phonics");
  const [bookList, setBookList] = useState(null);
  const [series, setSeries] = useState(null);
  const [canAssign, setCanAssign] = useState(false);
  const [planMessage, setPlanMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const kindInfo = KIND_LIST.find((one) => one.key === kind) || KIND_LIST[0];

  useEffect(() => {
    let isCurrent = true;
    setBookList(null);
    apiClient
      .get(`/api/v1/official-books?kind=${kind}`, { withAuth: true })
      .then((response) => {
        if (!isCurrent) return;
        setBookList(response.data.books || []);
        setSeries(response.data.series || null);
        setCanAssign(Boolean(response.data.can_assign));
        setPlanMessage(response.data.plan_required_message || "");
      })
      .catch(() => isCurrent && setBookList([]));
    return () => {
      isCurrent = false;
    };
  }, [kind]);

  const openBook = async (book) => {
    // 체험 교재(서버 OFFICIAL_BOOK_TRIAL_GINEDU_ID_LIST)는 책마다 can_assign 이 따로 온다
    if (!canAssign && !book.can_assign) {
      alert(planMessage);
      return;
    }
    await dispatch(retrieveWorksheet({ id: book.id, type: "readers_book", author_name: "지인에듀" }));
    setIsModalOpen(true);
  };

  return (
    <div className={`official-book official-book--${kind}`}>
      <section className="official-book__hero">
        <div className="official-book__hero-text">
          <span className="official-book__eyebrow">지인에듀 공식 교재</span>
          <h2>
            {kindInfo.english}
            {series?.title && kind === "phonics" && <small>{series.title}</small>}
          </h2>
          <p>종이책으로 수업하고, 같은 책을 온라인 숙제로 내 주세요.</p>
          <div className="official-book__tabs">
            {KIND_LIST.map((one) => (
              <button
                type="button"
                key={one.key}
                className={`official-book__tab${kind === one.key ? " is-now" : ""}`}
                onClick={() => setKind(one.key)}
              >
                {one.label}
              </button>
            ))}
          </div>
        </div>
        {/* 띠 오른쪽에 책 세 권을 펼쳐 놓는다 */}
        {bookList && bookList.length > 0 && (
          <div className="official-book__fan" aria-hidden="true">
            {bookList
              .filter((book) => book.cover_url)
              .slice(0, 3)
              .map((book, index) => (
                <img key={book.id} src={book.cover_url} alt="" style={{ "--fan-index": index }} />
              ))}
          </div>
        )}
      </section>

      <ul className="official-book__bridge">
        {BRIDGE_LIST.map((one) => (
          <li key={one.title}>
            <BridgeIcon name={one.icon} />
            <div>
              <b>{one.title}</b>
              <span>{one.body}</span>
            </div>
          </li>
        ))}
      </ul>

      {bookList === null && <p className="official-book__empty">불러오는 중이에요.</p>}
      {bookList && bookList.length === 0 && <p className="official-book__empty">곧 열려요.</p>}
      {bookList && bookList.length > 0 && (
        <div className="official-book__shelf">
          {bookList.map((book) => {
            const [unitLabel, bookTitle] = splitTitle(book.title);
            const soundList = book.sounds || [];
            return (
              <button type="button" key={book.id} className="official-book__card" onClick={() => openBook(book)}>
                <div className="official-book__cover">
                  {book.cover_url ? <img src={book.cover_url} alt="" loading="lazy" /> : null}
                  {(unitLabel || book.code) && <span className="official-book__unit">{unitLabel || book.code}</span>}
                  {book.is_trial && <span className="official-book__trial">체험</span>}
                  {/* 미리보기는 볼 수 있는 책에만(출제 계정은 모두, Plus · Premium 은 리더스 레벨별 1권 · 파닉스 Unit 15). 출제하기는 누구에게나 보이고
                      출제할 수 없는 계정이 누르면 안내만 뜬다(2026-10-20 대표) */}
                  <span className="official-book__actions">
                    {book.can_preview && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="official-book__action official-book__action--preview"
                        onClick={(event) => {
                          event.stopPropagation();
                          openOfficialBookPreview(book.id, user);
                        }}
                      >
                        미리보기
                      </span>
                    )}
                    <span className="official-book__action">출제하기</span>
                  </span>
                </div>
                <span className="official-book__title">{bookTitle}</span>
                {soundList.length > 0 && (
                  <span className="official-book__sounds">
                    {soundList.slice(0, 8).map((sound) => (
                      <i key={sound}>{sound}</i>
                    ))}
                    {soundList.length > 8 && <em>+{soundList.length - 8}</em>}
                  </span>
                )}
                <span className="official-book__meta">
                  {book.page_count}쪽
                  {book.field_count > 0 && ` · 채점 ${book.field_count}칸`}
                  {book.video_count > 0 && ` · 입모양 ${book.video_count}`}
                  {book.word_count > 0 && ` · 단어 ${book.word_count}`}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isModalOpen && <WorksheetModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
};

const BridgeIcon = ({ name }) => (
  <svg className="official-book__bridge-icon" viewBox="0 0 24 24" aria-hidden="true">
    {name === "book" && (
      <path d="M4 5.5C6.5 4.5 9 4.5 12 6c3-1.5 5.5-1.5 8-.5v13c-2.5-1-5-1-8 .5-3-1.5-5.5-1.5-8-.5zM12 6v13.5" />
    )}
    {name === "sound" && (
      <>
        <path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z" />
        <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
      </>
    )}
    {name === "pen" && (
      <>
        <path d="M5 19l1-4L15.5 5.5l3 3L9 18z" />
        <path d="M13.5 7.5l3 3" />
      </>
    )}
  </svg>
);

export default OfficialBookPage;
